/**
 * Item 7b (TASK049 / TASK042): disclose the 4% security-deposit claim processing fee
 * inside the Terms of Service.
 *
 * The Terms of Service page content is a hosted JSON asset served from Sharetribe
 * (`content/pages/terms-of-service.json`) — it does NOT live in this repo, so the clause
 * cannot be added by editing a static file. Instead we inject a section into the fetched
 * page-asset data at render time, in the same page-asset shape the SectionBuilder expects
 * (see FallbackPage.js). This keeps the disclosure in code / version control and applies
 * it everywhere the ToS is rendered (the /terms-of-service page AND the signup ToS modal).
 *
 * Source of truth for the percentage: server/config/depositConfig.js
 * (`CLAIM_PROCESSING_FEE_PERCENT = 0.04`). Keep this copy in sync if that value changes.
 */

export const CLAIM_FEE_SECTION_ID = 'claim-processing-fee';

const claimFeeMarkdown = `If an owner files a claim against your security deposit (for example for damage, loss, or a late return), a **claim processing fee of 4% of the claimed amount** is added to the total captured from your security deposit. This fee covers the administrative and payment-processing cost of reviewing and settling the claim.

The total amount captured — the claim amount plus the 4% processing fee — will never exceed the security deposit you authorized at booking, and the full breakdown is shown to you when the claim is processed.`;

// A valid page-asset "article" section (same schema as FallbackPage.js fallbackSections).
// Uses a heading2 title so it renders as a sub-section and never introduces a second <h1>.
export const CLAIM_FEE_SECTION = {
  sectionType: 'article',
  sectionId: CLAIM_FEE_SECTION_ID,
  appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
  title: { fieldType: 'heading2', content: 'Security Deposit Claim Fee' },
  blocks: [
    {
      blockType: 'defaultBlock',
      blockId: 'claim-processing-fee-content',
      text: {
        fieldType: 'markdown',
        content: claimFeeMarkdown,
      },
    },
  ],
};

/**
 * Append the claim-fee disclosure section to a fetched Terms of Service page asset.
 *
 * Idempotent and defensive: returns the input unchanged when there are no sections to
 * append to (e.g. the fetch failed and a fallback is used) or when the section is already
 * present (e.g. it was later added in Console) — so the clause can never be duplicated.
 *
 * @param {Object|null|undefined} pageData denormalized page-asset data ({ sections, meta })
 * @returns {Object|null|undefined} pageData with the claim-fee section appended
 */
export const withClaimFeeClause = pageData => {
  if (!pageData || !Array.isArray(pageData.sections)) {
    return pageData;
  }
  const alreadyPresent = pageData.sections.some(
    section => section?.sectionId === CLAIM_FEE_SECTION_ID
  );
  if (alreadyPresent) {
    return pageData;
  }
  return { ...pageData, sections: [...pageData.sections, CLAIM_FEE_SECTION] };
};
